import React, { useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { EMPTY_NOTE_TEXT, Ic, PageHeader, Spinner, fmt, friendlyDate, noteSeconds, recordingName, recordingSeconds } from "./notesShared";

// Notes shown inside a card before it collapses to "+N more".
const PREVIEW_NOTES = 3;

const FILTERS = [
    { id: "all", label: "All" },
    { id: "with", label: "With notes" },
    { id: "without", label: "Without notes" },
];

const EMPTY_TEXT = {
    all: "No recordings yet. Record one with notes to get started.",
    with: "None of your recordings have notes yet.",
    without: "Every recording has at least one note.",
};

// Every recording as a card with its notes, filterable by whether it has any.
const MyNotes = ({ recordings, folderNames, loadingFolders, importing, onBack, onOpenNote, onOpenRecording, onRecord, onPickRecording }) => {
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("all");

    const noteCount = (audio) => audio?.notes?.length || 0;
    const counts = {
        all: recordings.length,
        with: recordings.filter((audio) => noteCount(audio) > 0).length,
        without: recordings.filter((audio) => noteCount(audio) === 0).length,
    };

    const q = query.toLowerCase().trim();
    const cards = recordings
        .filter((audio) => filter === "all" || (filter === "with" ? noteCount(audio) > 0 : noteCount(audio) === 0))
        .map((audio) => {
            const notes = audio?.notes || [];
            if (!q || recordingName(audio).toLowerCase().includes(q)) return { audio, notes };
            // A search that only matches note text narrows the card to those notes.
            const matching = notes.filter((note) => (note.content || "").toLowerCase().includes(q));
            return matching.length ? { audio, notes: matching } : null;
        })
        .filter(Boolean)
        .sort((a, b) => new Date(b.audio.createdAt) - new Date(a.audio.createdAt));

    const actions = (
        <>
            <button type="button" className="nt-btn outline" disabled={importing} onClick={onPickRecording}>
                {importing ? <><Spinner />Importing…</> : <><Ic name="noteplus" size="s" />Add note to a recording</>}
            </button>
            <button type="button" className="nt-btn red" onClick={onRecord}>
                <Ic name="mic" size="s" />Record with notes
            </button>
        </>
    );

    return (
        <>
            <PageHeader title="My Notes" onBack={onBack} actions={actions} icon={<Icon icon="lucide:notebook-pen" />} tone="amber" />
            <div className="nt-content">
                <p className="nt-subtitle">All your recordings and the notes on them. Click a note to play the recording from that moment.</p>

                <div className="nt-toolbar">
                    <div className="nt-search">
                        <Ic name="search" size="s" />
                        <input
                            type="search"
                            placeholder="Search recordings or notes"
                            aria-label="Search recordings or notes"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Escape") setQuery(""); }}
                        />
                        {query && (
                            <button type="button" className="nt-search-clear" aria-label="Clear search" onClick={() => setQuery("")}>
                                <Ic name="close" size="s" />
                            </button>
                        )}
                    </div>
                    <div className="nt-chips" role="tablist">
                        {FILTERS.map((item) => (
                            <button
                                type="button"
                                key={item.id}
                                role="tab"
                                aria-selected={filter === item.id}
                                className={`nt-chip ${filter === item.id ? "on" : ""}`}
                                onClick={() => setFilter(item.id)}
                            >
                                {item.label}<span className="nt-chip-count">{counts[item.id]}</span>
                            </button>
                        ))}
                    </div>
                    {loadingFolders && <span className="nt-tiny-muted">Loading folder recordings…</span>}
                    {q && cards.length > 0 && (
                        <span className="nt-tiny-muted nt-results" aria-live="polite">
                            {cards.length} result{cards.length > 1 ? "s" : ""}
                        </span>
                    )}
                </div>

                {cards.length ? (
                    <div className="nt-recgrid vr-stagger">
                        {cards.map(({ audio, notes }) => {
                            const total = noteCount(audio);
                            const folderName = audio.folderId ? folderNames[audio.folderId] : null;
                            return (
                                <div className="nt-reccell" key={audio._id}>
                                    <div
                                        className={`nt-card nt-reccard ${total ? "" : "empty"}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => onOpenRecording(audio)}
                                        onKeyDown={(e) => { if (e.key === "Enter") onOpenRecording(audio); }}
                                    >
                                        <div className="nt-reccard-head">
                                            <span className={`nt-av ${total ? "" : "grey"}`}><Ic name="mic" /></span>
                                            <div className="nt-grow">
                                                <p className="nt-title" title={recordingName(audio)}>{recordingName(audio)}</p>
                                                <span className="nt-sub">
                                                    {friendlyDate(audio.createdAt)} • {fmt(recordingSeconds(audio))}
                                                </span>
                                            </div>
                                            {total > 0 && (
                                                <span className="nt-tag amber">{`${total} note${total > 1 ? "s" : ""}`}</span>
                                            )}
                                            <span className="nt-reccard-go" aria-hidden="true"><Ic name="chev" size="s" /></span>
                                        </div>
                                        {folderName && <span className="nt-tag nt-folder"><Ic name="folder" size="s" />{folderName}</span>}

                                        {total ? (
                                            <div className="nt-reccard-notes">
                                                {notes.slice(0, PREVIEW_NOTES).map((note) => (
                                                    <button
                                                        type="button"
                                                        key={note._id}
                                                        className="nt-noterow"
                                                        title="Play from here"
                                                        // Stays on the note's moment instead of the card's default.
                                                        onClick={(e) => { e.stopPropagation(); onOpenNote(audio, noteSeconds(note)); }}
                                                    >
                                                        <span className="nt-ts">{fmt(noteSeconds(note))}</span>
                                                        <span className="nt-noterow-text">{note.content || EMPTY_NOTE_TEXT}</span>
                                                    </button>
                                                ))}
                                                {notes.length > PREVIEW_NOTES && (
                                                    <span className="nt-more">+{notes.length - PREVIEW_NOTES} more</span>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="nt-reccard-empty">
                                                <span className="nt-reccard-hint">No notes yet</span>
                                                <span className="nt-reccard-add"><Ic name="plus" size="s" />Add notes</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="nt-empty">
                        <div className="nt-av nt-empty-tile"><Ic name={q ? "search" : "note"} size="xl" /></div>
                        <h5>{q ? "Nothing found" : "Nothing here"}</h5>
                        <p>{q ? `No recordings or notes match “${query.trim()}”.` : EMPTY_TEXT[filter]}</p>
                        {!q && filter !== "without" && <div className="nt-actions nt-actions-center">{actions}</div>}
                    </div>
                )}
            </div>
        </>
    );
};

export default MyNotes;
